import SportIcon from '@/components/SportIcon';
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Trophy, Users, CalendarDays, Target, UserCircle,
  ArrowUpRight, Clock, Activity, UserCog, BarChart2,
  ChevronRight, Star, Swords, TrendingUp
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

// ── Sport constants ────────────────────────────────────────────
const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽', default:'🏅',
};
const SPORT_LABELS = {
  table_tennis:'Table Tennis', badminton:'Badminton', chess:'Chess',
  carrom:'Carrom', pickleball:'Pickleball', squash:'Squash',
  cricket:'Cricket', football:'Football',
};

const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit', hour12:true, timeZone:'Asia/Kolkata' })
  : 'TBD';

// ── Admin stat tile ────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, accent, loading, to }) => (
  <Link to={to} className="block group">
    <Card className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.5)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer h-full">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[#8a93a3] mb-1 group-hover:text-[#f0f2f7] transition-colors">{title}</p>
            {loading ? <Skeleton className="h-8 w-16 bg-[#1d2230]"/> :
              <p className="text-3xl font-bold text-[#f0f2f7] tabular-nums">{value ?? '—'}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={`p-2 rounded-lg ${accent ? 'bg-[rgba(34,197,94,0.12)]' : 'bg-[#1d2230]'} group-hover:scale-110 transition-transform`}>
              <Icon className={`h-5 w-5 ${accent ? 'text-[#22c55e]' : 'text-[#f5c518]'}`}/>
            </div>
            <ArrowUpRight className="h-3.5 w-3.5 text-[#8a93a3] opacity-0 group-hover:opacity-100 transition-opacity"/>
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const QuickAction = ({ to, icon: Icon, label, description }) => (
  <Link to={to}>
    <Card className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)] transition-all hover:-translate-y-0.5 cursor-pointer h-full">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="p-3 rounded-lg bg-[rgba(245,197,24,0.1)] flex-shrink-0">
          <Icon className="h-5 w-5 text-[#f5c518]"/>
        </div>
        <div>
          <p className="font-medium text-[#f0f2f7]">{label}</p>
          <p className="text-xs text-[#8a93a3]">{description}</p>
        </div>
      </CardContent>
    </Card>
  </Link>
);

// ── Colourful member action card ───────────────────────────────
function MemberAction({ to, href, icon, label, sub, gradient, border }) {
  const cls = `group relative overflow-hidden rounded-2xl border p-5 cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg ${border}`;
  const inner = (
    <div className={`absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity ${gradient}`}/>
  );
  const content = (
    <>
      {inner}
      <div className="relative">
        <div className="text-4xl mb-3">{icon}</div>
        <p className="font-black text-[#f0f2f7] text-base">{label}</p>
        <p className="text-xs text-[#8a93a3] mt-0.5">{sub}</p>
        <ChevronRight className="h-4 w-4 text-[#8a93a3] group-hover:text-[#f0f2f7] mt-3 transition-colors"/>
      </div>
    </>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls}>{content}</a>;
  return <Link to={to} className={cls}>{content}</Link>;
}

// ── Member dashboard ──────────────────────────────────────────
function MemberDashboard({ user, orgSlug, org, allTournaments, firstTournamentId, setFirstTournamentId }) {
  const [profile, setProfile] = useState(null);
  const [pLoading, setPLoading] = useState(true);
  const token = () => localStorage.getItem('btccp_access_token');

  useEffect(() => {
    fetch(`/api/orgs/${orgSlug}/me/member-profile`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProfile(d); })
      .catch(() => {})
      .finally(() => setPLoading(false));
  }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstName = user?.display_name?.split(' ')[0] || 'Player';
  const team      = profile?.team;
  const sports    = profile?.player?.sports || [];
  const upcoming  = profile?.upcoming || [];
  const stats     = profile?.stats;
  const perSport  = profile?.per_sport || [];
  const teamColor = team?.color || '#f5c518';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto" data-testid="member-dashboard">

      {/* ── Hero banner ─────────────────────────────────────── */}
      <div className="relative rounded-3xl overflow-hidden mb-6 p-6 pb-8"
        style={{ background: `linear-gradient(135deg, ${teamColor}22 0%, #10131c 60%)`,
                 border: `1px solid ${teamColor}30` }}>
        {/* Decorative sport emoji */}
        <div className="absolute right-16 top-3 flex gap-2">
          <img src="/circles-logo.png" alt="Circles" className="h-14 w-14 rounded-full object-cover opacity-90"/>
          <img src="/c7-logo.png" alt="C7" className="h-14 w-14 rounded-full object-cover opacity-90"/>
        </div>
        <div className="absolute right-4 top-4 text-5xl opacity-10 select-none">
          sports[0] ? '' : '🏅'
        </div>
        <p className="text-[#8a93a3] text-sm mb-1">
          {org?.name || 'Circles'} — {org?.tagline || 'growth with happiness'}
        </p>
        <h1 className="text-2xl sm:text-3xl font-black text-[#f0f2f7] mb-2">
          Welcome, {firstName}! 👋
        </h1>
        {/* Team pill */}
        {team ? (
          <div className="flex items-center gap-2 mt-3">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center font-black text-white text-xs overflow-hidden flex-shrink-0"
              style={{ backgroundColor: teamColor }}>
              {team.logo_url?.startsWith('data:')
                ? <img src={team.logo_url} className="h-full w-full object-cover" alt=""/>
                : team.name?.[0]}
            </div>
            <span className="text-sm font-bold text-[#f0f2f7]">{team.name}</span>
            {sports.length > 0 && (
              <div className="flex gap-1 ml-2">
                {sports.map(s => <span key={s} className="text-base" title={SPORT_LABELS[s] || s}>{SPORT_ICONS[s] || '🏅'}</span>)}
              </div>
            )}
          </div>
        ) : pLoading ? (
          <Skeleton className="h-7 w-40 bg-[#1d2230] mt-3 rounded-xl"/>
        ) : null}
      </div>

      {/* ── Quick actions 2×2 grid ─────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <MemberAction to="/admin/profile" icon="👤" label="My Profile"
          sub="Stats · team · matches" gradient="bg-[#f5c518]"
          border="border-[rgba(245,197,24,0.3)] bg-[#0f1219]"/>
        <MemberAction to="/admin/rankings" icon="🏆" label="Rankings"
          sub="Leaderboard · sport-wise" gradient="bg-[#22c55e]"
          border="border-[rgba(34,197,94,0.3)] bg-[#0f1219]"/>
        <MemberAction
          href={firstTournamentId ? `/public/tournaments/${firstTournamentId}` : undefined}
          to={firstTournamentId ? undefined : '/admin/tournaments'}
          icon="📊" label="Live Scoreboard"
          sub="Standings · fixtures · results" gradient="bg-[#3b82f6]"
          border="border-[rgba(59,130,246,0.3)] bg-[#0f1219]"/>
      </div>

      {/* ── Stats pills ─────────────────────────────────────── */}
      {stats && stats.played > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-6">
          {[
            { l:'Played', v: stats.played,   c:'#8a93a3' },
            { l:'Wins',   v: stats.wins,     c:'#22c55e' },
            { l:'Losses', v: stats.losses,   c:'#ef4444' },
            { l:'Win %',  v:`${stats.win_pct}%`, c:'#f5c518' },
          ].map(({ l, v, c }) => (
            <div key={l} className="bg-[#161a26] border border-[#252a37] rounded-2xl p-3 text-center">
              <p className="text-xl font-black tabular-nums" style={{ color: c }}>{v}</p>
              <p className="text-[10px] text-[#8a93a3] uppercase tracking-wide mt-0.5">{l}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Per-sport performance ────────────────────────────── */}
      {perSport.length > 0 && (
        <Card className="bg-[#10131c] border-[#252a37] mb-6">
          <CardContent className="p-4">
            <p className="text-xs font-bold text-[#8a93a3] uppercase tracking-wider mb-3 flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5"/> Sport-wise Performance
            </p>
            <div className="space-y-2.5">
              {perSport.map(ps => {
                const pct = ps.win_pct || 0;
                return (
                  <div key={ps.sport_code}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#f0f2f7] flex items-center gap-1.5">
                        {SPORT_ICONS[ps.sport_code] || '🏅'} {SPORT_LABELS[ps.sport_code] || ps.sport_code}
                      </span>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-[#22c55e] font-bold">{ps.wins}W</span>
                        <span className="text-[#8a93a3]">{ps.draws}D</span>
                        <span className="text-[#ef4444]">{ps.losses}L</span>
                        <span className="text-[#f5c518] font-black w-9 text-right">{pct}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#1d2230] rounded-full overflow-hidden">
                      <div className="h-full rounded-full"
                        style={{ width:`${pct}%`,
                          backgroundColor: pct >= 60 ? '#22c55e' : pct >= 40 ? '#f5c518' : '#ef4444' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Upcoming matches ──────────────────────────────────── */}
      {upcoming.length > 0 && (
        <Card className="bg-[#10131c] border-[#252a37] mb-6">
          <CardContent className="p-4">
            <p className="text-xs font-bold text-[#8a93a3] uppercase tracking-wider mb-3 flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5"/> Upcoming Matches
            </p>
            <div className="space-y-2">
              {upcoming.slice(0, 4).map((enc, i) => (
                <div key={enc.id || i}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                    enc.status === 'in_progress'
                      ? 'bg-[rgba(239,68,68,0.05)] border-[rgba(239,68,68,0.3)]'
                      : 'bg-[#161a26] border-[#252a37]'
                  }`}>
                  <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-white text-sm overflow-hidden"
                    style={{ backgroundColor: enc.opponent?.color || '#3b82f6' }}>
                    {enc.opponent?.logo_url?.startsWith('data:')
                      ? <img src={enc.opponent.logo_url} className="h-full w-full object-cover" alt=""/>
                      : (enc.opponent?.name || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#f0f2f7] truncate">
                      vs {enc.opponent?.name || 'TBD'}
                    </p>
                    <p className="text-xs text-[#8a93a3]">{fmtTime(enc.scheduled_at)}</p>
                  </div>
                  {enc.status === 'in_progress' && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-[#ef4444] flex-shrink-0">
                      <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse"/>LIVE
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Tournament picker + scoreboard link ─────────────── */}
      {allTournaments.length > 0 && firstTournamentId && (
        <div className="flex items-center gap-3">
          {allTournaments.length > 1 && (
            <select value={firstTournamentId} onChange={e => setFirstTournamentId(e.target.value)}
              className="flex-1 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] rounded-xl px-3 py-2 text-sm">
              {allTournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          <a href={`/public/tournaments/${firstTournamentId}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] text-[#22c55e] text-sm font-semibold hover:bg-[rgba(34,197,94,0.15)] transition-colors whitespace-nowrap">
            🏆 Open Scoreboard ↗
          </a>
        </div>
      )}
    </div>
  );
}

// ── Main dashboard page ───────────────────────────────────────
export default function DashboardPage() {
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin   = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';

  const [stats, setStats]               = useState(null);
  const [org, setOrg]                   = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [firstTournamentId, setFirstTournamentId] = useState('');
  const [allTournaments, setAllTournaments] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [orgRes, teamsRes, playersRes, tournamentsRes, usersRes, auditRes, rosterRes] =
          await Promise.allSettled([
            api.get(`/orgs/${orgSlug}`),
            api.get(`/orgs/${orgSlug}/teams`),
            api.get(`/orgs/${orgSlug}/players/count`),
            api.get(`/orgs/${orgSlug}/tournaments`),
            api.get(`/orgs/${orgSlug}/users`),
            api.get(`/orgs/${orgSlug}/audit?limit=6`),
            api.get(`/orgs/${orgSlug}/tournament-player-count`),
          ]);

        if (orgRes.status === 'fulfilled') setOrg(orgRes.value.data);

        if (tournamentsRes.status === 'fulfilled') {
          const tourns = Array.isArray(tournamentsRes.value.data) ? tournamentsRes.value.data : [];
          setAllTournaments(tourns);
          if (tourns.length > 0) setFirstTournamentId(tourns[0].id);
        }

        const count = (res) => {
          if (res.status !== 'fulfilled') return 0;
          const d = res.value.data;
          return Array.isArray(d) ? d.length : d?.count ?? 0;
        };

        setStats({ teams: count(teamsRes), players: count(playersRes),
                   tournaments: count(tournamentsRes),
                   users: rosterRes.status === 'fulfilled' ? (rosterRes.value.data?.count ?? count(usersRes)) : count(usersRes) });

        if (auditRes.status === 'fulfilled') {
          const d = auditRes.value.data;
          setRecentActivity(Array.isArray(d) ? d.slice(0, 6) : d?.items?.slice(0, 6) ?? []);
        }
      } catch { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [orgSlug]);

  // ── Member view ────────────────────────────────────────────
  if (!admin) {
    return (
      <MemberDashboard
        user={user} orgSlug={orgSlug} org={org}
        allTournaments={allTournaments}
        firstTournamentId={firstTournamentId}
        setFirstTournamentId={setFirstTournamentId}
      />
    );
  }

  // ── Admin view ─────────────────────────────────────────────
  const formatAction = (action) =>
    action?.replace(/\./g, ' › ').replace(/_/g, ' ') ?? 'Activity';

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-2">
            Welcome back, {user?.display_name?.split(' ')[0] || 'Admin'}!
          </h1>
        <div className="text-[#8a93a3] flex items-center flex-wrap gap-2">
          {org?.name || 'Circles'} — {org?.tagline || 'growth with happiness'}
          {org?.plan && (
            <Badge className="bg-[rgba(245,197,24,0.1)] text-[#f5c518] border border-[rgba(245,197,24,0.3)] text-xs">
              {org.plan.toUpperCase()}
            </Badge>
          )}
        </div>
      </div>
        {/* Logos — top right */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          <img src="/circles-logo.png" alt="Circles" className="h-16 w-16 rounded-full object-cover flex-shrink-0"
            onError={e => { e.currentTarget.style.display='none'; }}/>
          <img src="/c7-logo.png" alt="C7" className="h-16 w-16 rounded-full object-cover flex-shrink-0"
            onError={e => { e.currentTarget.style.display='none'; }}/>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats-tiles">
        <StatCard title="Teams"             value={stats?.teams}       icon={Trophy}       loading={loading} to="/admin/teams" />
        <StatCard title="Members"           value={stats?.players}     icon={Users}        loading={loading} to="/admin/players" />
        <StatCard title="Tournaments"       value={stats?.tournaments} icon={CalendarDays} loading={loading} to="/admin/tournaments" />
        <StatCard title="Tournament Players" value={stats?.users}      icon={UserCog}      loading={loading} to="/admin/players" accent />
      </div>

      {/* Quick Actions */}
      <div className="mb-8" data-testid="dashboard-quick-actions">
        <h2 className="text-lg font-semibold text-[#f0f2f7] mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction to="/admin/users"       icon={UserCog}   label="Users"         description="Invite &amp; manage users" />
          <QuickAction to="/admin/tournaments" icon={Trophy}    label="Tournaments"   description="Create &amp; manage events" />
          <QuickAction to="/admin/players"     icon={Users}     label="Players"       description="Roster &amp; bulk upload" />
          <QuickAction to="/score"             icon={Target}    label="Scorer Portal" description="Start live scoring" />
        </div>
      </div>

      {/* Live Screens — admin sees both TV + Scoreboard */}
      {allTournaments.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-[#f0f2f7]">📡 Live Screens</h2>
            {allTournaments.length > 1 && (
              <select value={firstTournamentId} onChange={e => setFirstTournamentId(e.target.value)}
                className="bg-[#161a26] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-1 text-sm">
                {allTournaments.map(t => (
                  <option key={t.id} value={t.id}>{t.is_test ? '🧪 ' : ''}{t.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href={`/tv/${firstTournamentId}`} target="_blank" rel="noreferrer"
              className="group block rounded-2xl border border-[rgba(239,68,68,0.3)] bg-[#161a26] hover:bg-[rgba(239,68,68,0.06)] hover:-translate-y-0.5 transition-all p-5">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-[rgba(239,68,68,0.12)] flex items-center justify-center text-2xl flex-shrink-0">📺</div>
                <div className="min-w-0">
                  <p className="font-bold text-[#f0f2f7] group-hover:text-[#ef4444] transition-colors">Broadcast TV</p>
                  <p className="text-xs text-[#8a93a3] mt-0.5">ESPN-style full-screen · sponsor scroll · auto-rotates</p>
                </div>
                <span className="text-[#8a93a3] group-hover:text-[#ef4444] ml-auto flex-shrink-0">↗</span>
              </div>
            </a>
            <a href={`/public/tournaments/${firstTournamentId}`} target="_blank" rel="noreferrer"
              className="group block rounded-2xl border border-[rgba(34,197,94,0.3)] bg-[#161a26] hover:bg-[rgba(34,197,94,0.06)] hover:-translate-y-0.5 transition-all p-5">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-[rgba(34,197,94,0.12)] flex items-center justify-center text-2xl flex-shrink-0">🏆</div>
                <div className="min-w-0">
                  <p className="font-bold text-[#f0f2f7] group-hover:text-[#22c55e] transition-colors">Live Scoreboard</p>
                  <p className="text-xs text-[#8a93a3] mt-0.5">Standings · Live matches · Schedule · drill-down</p>
                </div>
                <span className="text-[#8a93a3] group-hover:text-[#22c55e] ml-auto flex-shrink-0">↗</span>
              </div>
            </a>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div data-testid="dashboard-activity-feed">
        <h2 className="text-lg font-semibold text-[#f0f2f7] mb-4">Recent Activity</h2>
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-full bg-[#1d2230]"/>
                    <div className="flex-1">
                      <Skeleton className="h-4 w-3/4 bg-[#1d2230] mb-1"/>
                      <Skeleton className="h-3 w-1/2 bg-[#1d2230]"/>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="divide-y divide-[#252a37]">
                {recentActivity.map((item, idx) => (
                  <div key={item.id || idx} className="p-4 flex items-center gap-3 hover:bg-[#1d2230]/50 transition-colors">
                    <div className="p-2 rounded-full bg-[#1d2230] flex-shrink-0">
                      <Activity className="h-4 w-4 text-[#8a93a3]"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#f0f2f7] truncate capitalize">{formatAction(item.action)}</p>
                      <p className="text-xs text-[#8a93a3]">
                        {item.actor_name || 'System'} · {item.at ? new Date(item.at).toLocaleString() : 'Recently'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Clock className="h-12 w-12 text-[#8a93a3] mx-auto mb-3"/>
                <p className="text-[#8a93a3]">No recent activity</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
